import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useBreadcrumbs } from "@/hooks/useBreadcrumbs";
import { getFirmApiToken, registerFirmAPI } from "@/sideEffects/api";
import { selectActiveFirm } from "@/store/appSelectors";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { useForm } from "react-hook-form";
import { showApiError, showSuccess } from "@/lib/toast";
import { Textarea } from "@/components/ui/textarea";
import { ROUTES } from "@/constants/routeConstants";
import { LoadingSmall } from "@/components/LoadingSmall";
import { BaseFormLayout } from "./shared/BaseFormLayout";

const formSchema = z.object({
  code: z.string().optional(),
  expiry: z.string().optional(),
  token: z.string().optional(),
  status: z.string().optional(),
});

type FormSchemaType = z.infer<typeof formSchema>;

export const FirmTokenPage = () => {
  const { t } = useTranslation();
  const activeFirm = useSelector(selectActiveFirm);
  const firmGuid = activeFirm?.firmGuid;

  useBreadcrumbs([{ name: t("firmToken.title"), path: ROUTES.FIRM_TOKEN }]);

  const {
    data,
    isLoading: isFirmTokenLoading,
    refetch,
  } = useQuery({
    queryKey: ["firmToken", firmGuid],
    queryFn: () => getFirmApiToken(String(firmGuid)),
  });

  const firmTokenData = data?.data;

  const form = useForm<FormSchemaType>({
    resolver: zodResolver(formSchema),
    values: {
      code: firmGuid,
      expiry: firmTokenData?.expiry,
      token: firmTokenData?.token,
      status: firmTokenData?.status,
    },
  });

  const requestNewApiTokenMutation = useMutation({
    mutationFn: () => {
      return registerFirmAPI(String(firmGuid));
    },
    onSuccess: () => {
      showSuccess(t("common.createdSuccess"));
      refetch();
    },
    onError: showApiError,
  });

  function onRequestNewApiToken() {
    requestNewApiTokenMutation.mutate();
  }

  return (
    <BaseFormLayout title={t("firmToken.title")}>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onRequestNewApiToken)} className="space-y-8">
          <FormField
            control={form.control}
            name="code"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("firmToken.code")}</FormLabel>
                <FormControl>
                  <Input {...field} disabled />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="status"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("firmToken.status")}</FormLabel>
                <FormControl>
                  <Input {...field} disabled />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="expiry"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("firmToken.expiry")}</FormLabel>
                <FormControl>
                  <Input {...field} disabled />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="token"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("firmToken.token")}</FormLabel>
                <FormControl>
                  <Textarea rows={6} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button type="submit" disabled={isFirmTokenLoading || requestNewApiTokenMutation.isPending}>
            {t("firmToken.requestNewToken")}
            {(isFirmTokenLoading || requestNewApiTokenMutation.isPending) && <LoadingSmall />}
          </Button>
        </form>
      </Form>
    </BaseFormLayout>
  );
};
